// Mock data for the Attendance nav tab (Numbers, Trend, Comparison,
// Alarming, Caution). Shapes feed the shared components directly.

// ═════════════════════════════════════════════
// Numbers
// ═════════════════════════════════════════════

export const ATT_KPIS = {
  markingCompliance: 82.5,
  studentAttendance: 88.7,
  teacherAttendance: 93.2,
  dropoutRisk: 3.88,
};

export const ATT_METRICS = {
  schools: 16534,
  studentsEnrolled: 4819395,
  absenteeDays: 13569170,
  unmarkedSchoolDays: 86751,
  potentialDropouts: 187162,
};

export interface AttBandRow {
  band: string;
  schools: number;
  shareSchools: string;
  students: number;
  shareStudents: string;
  color: string;
  [key: string]: unknown;
}

export const ATTENDANCE_BANDS: AttBandRow[] = [
  {
    band: 'Below 60%',
    schools: 110,
    shareSchools: '0.7%',
    students: 31092,
    shareStudents: '0.6%',
    color: '#c0392b',
  },
  {
    band: '60-75%',
    schools: 182,
    shareSchools: '1.1%',
    students: 48540,
    shareStudents: '1%',
    color: '#e4572e',
  },
  {
    band: '75-85%',
    schools: 4417,
    shareSchools: '26.7%',
    students: 1295745,
    shareStudents: '26.9%',
    color: '#e8a33d',
  },
  {
    band: '85-90%',
    schools: 4850,
    shareSchools: '29.3%',
    students: 1392374,
    shareStudents: '28.9%',
    color: '#d4c832',
  },
  {
    band: '90-95%',
    schools: 4170,
    shareSchools: '25.2%',
    students: 1209438,
    shareStudents: '25.1%',
    color: '#8bc34a',
  },
  {
    band: '95% & above',
    schools: 2805,
    shareSchools: '17%',
    students: 842206,
    shareStudents: '17.5%',
    color: '#2e7d32',
  },
];

export interface SchoolTypeRow {
  type: string;
  schools: number;
  students: number;
  attendance: string;
  compliance: string;
  teacher: string;
  dropoutRisk: string;
  [key: string]: unknown;
}

export const SCHOOL_TYPES: SchoolTypeRow[] = [
  {
    type: 'Government',
    schools: 12913,
    students: 3188783,
    attendance: '88.4%',
    compliance: '82.6%',
    teacher: '92.6%',
    dropoutRisk: '3.8%',
  },
  {
    type: 'Fully Aided',
    schools: 2771,
    students: 1123314,
    attendance: '87.3%',
    compliance: '83.9%',
    teacher: '92.7%',
    dropoutRisk: '4.2%',
  },
  {
    type: 'Partially Aided',
    schools: 850,
    students: 507298,
    attendance: '88.4%',
    compliance: '82.7%',
    teacher: '92.5%',
    dropoutRisk: '3.8%',
  },
];

// Grouped-bar view of the school-type numbers.
export const SCHOOL_TYPE_CHART = {
  types: ['Government', 'Fully Aided', 'Partially Aided'],
  studentAttendance: [88.4, 87.3, 88.4],
  markingCompliance: [82.6, 83.9, 82.7],
  teacherAttendance: [92.6, 92.7, 92.5],
};

export const MARKED_VS_UNMARKED = { marked: 82.51, unmarked: 17.49 };
export const PRESENT_VS_ABSENT = { present: 88.68, absent: 11.32 };

export const ACROSS_PERIODS = {
  periods: ['Last 3d', 'Last 5d', 'Last 7d', 'Last 15d', 'Last 30d'],
  //here
  attendancePct: [88.5, 88.5, 88.5, 88.6, 88.7],
  absenteeDays: [1380968, 2292923, 3214883, 6849720, 13569170],
};

export interface PeriodRow {
  period: string;
  attendance: string;
  absenteeDays: number;
  compliance: string;
  unmarkedDays: number;
  [key: string]: unknown;
}

export const PERIOD_BREAKDOWN: PeriodRow[] = [
  {
    period: 'Last 3 working days',
    attendance: '88.5%',
    absenteeDays: 1380968,
    compliance: '82.6%',
    unmarkedDays: 8643,
  },
  {
    period: 'Last 5 working days',
    attendance: '88.5%',
    absenteeDays: 2292923,
    compliance: '82.5%',
    unmarkedDays: 14466,
  },
  {
    period: 'Last 7 working days',
    attendance: '88.5%',
    absenteeDays: 3214883,
    compliance: '82.6%',
    unmarkedDays: 20144,
  },
  {
    period: 'Last 15 working days',
    attendance: '88.6%',
    absenteeDays: 6849720,
    compliance: '82.5%',
    unmarkedDays: 43306,
  },
  {
    period: 'Last 30 working days',
    attendance: '88.7%',
    absenteeDays: 13569170,
    compliance: '82.5%',
    unmarkedDays: 86751,
  },
];

export interface AttDistrictRow {
  name: string;
  attendance: string;
  compliance: string;
  teacher: string;
  absenteeDays: number;
  dropoutRisk: string;
  [key: string]: unknown;
}

export const ATT_DISTRICT_SUMMARY: AttDistrictRow[] = [
  {
    name: 'Tiruvallur',
    attendance: '84.3%',
    compliance: '72.8%',
    teacher: '95%',
    absenteeDays: 488341,
    dropoutRisk: '5.01%',
  },
  {
    name: 'Chennai',
    attendance: '94.2%',
    compliance: '88.2%',
    teacher: '93.8%',
    absenteeDays: 367468,
    dropoutRisk: '1.38%',
  },
  {
    name: 'Kancheepuram',
    attendance: '84.9%',
    compliance: '77.7%',
    teacher: '88.1%',
    absenteeDays: 267514,
    dropoutRisk: '5.6%',
  },
  {
    name: 'Chengalpattu',
    attendance: '91.4%',
    compliance: '92.6%',
    teacher: '95.5%',
    absenteeDays: 293118,
    dropoutRisk: '3.08%',
  },
  {
    name: 'Ranipet',
    attendance: '93.9%',
    compliance: '73.6%',
    teacher: '90.6%',
    absenteeDays: 116961,
    dropoutRisk: '0.8%',
  },
  {
    name: 'Vellore',
    attendance: '81.8%',
    compliance: '90.6%',
    teacher: '88.1%',
    absenteeDays: 514840,
    dropoutRisk: '6.97%',
  },
  {
    name: 'Tirupathur',
    attendance: '86.3%',
    compliance: '94.2%',
    teacher: '95.7%',
    absenteeDays: 360753,
    dropoutRisk: '5.93%',
  },
  {
    name: 'Dharmapuri',
    attendance: '94.8%',
    compliance: '90.9%',
    teacher: '90.6%',
    absenteeDays: 139767,
    dropoutRisk: '1.22%',
  },
  {
    name: 'Tiruvannamalai',
    attendance: '86.7%',
    compliance: '74.3%',
    teacher: '91%',
    absenteeDays: 530208,
    dropoutRisk: '4.2%',
  },
  {
    name: 'Villupuram',
    attendance: '95.5%',
    compliance: '72.8%',
    teacher: '88%',
    absenteeDays: 133849,
    dropoutRisk: '0.47%',
  },
  {
    name: 'Kallakurichi',
    attendance: '86.9%',
    compliance: '75.3%',
    teacher: '92.2%',
    absenteeDays: 370383,
    dropoutRisk: '4.39%',
  },
  {
    name: 'Salem',
    attendance: '90.8%',
    compliance: '79.4%',
    teacher: '94.5%',
    absenteeDays: 547830,
    dropoutRisk: '2.65%',
  },
];

// ═════════════════════════════════════════════
// Trend Analysis
// ═════════════════════════════════════════════

// Generate ~30 working-day dates ending 09-24 with lightly noisy series.
const buildDates = (): string[] => {
  const out: string[] = [];
  const end = new Date(2026, 8, 24); // 24 Sep 2026
  const d = new Date(end);
  const dates: string[] = [];
  let count = 0;
  while (count < 30) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      dates.push(`${mm}-${dd}`);
      count++;
    }
    d.setDate(d.getDate() - 1);
  }
  dates.reverse();
  out.push(...dates);
  return out;
};

const wobble = (base: number, i: number, amp: number) =>
  Number(
    (base + Math.sin(i / 2.2) * amp + Math.cos(i / 3.7) * (amp / 2)).toFixed(1)
  );

export const TREND_DATES = buildDates();

export const DAILY_TREND = {
  dates: TREND_DATES,
  studentAttendance: TREND_DATES.map((_, i) => wobble(88.5, i, 0.9)),
  markingCompliance: TREND_DATES.map((_, i) => wobble(82.7, i, 1.1)),
  teacherAttendance: TREND_DATES.map((_, i) => wobble(93.2, i, 0.4)),
};

const LOWEST_NAMES = [
  'Vellore',
  'Tirunelveli',
  'Ariyalur',
  'Pudukkottai',
  'Tiruvallur',
  'Thoothukkudi',
  'Kancheepuram',
  'Theni',
  'Nagapattinam',
  'Coimbatore',
  'Tirupathur',
  'Cuddalore',
];
const LOWEST_COLORS = [
  '#e8a33d',
  '#e4572e',
  '#f2c14e',
  '#7b1e3a',
  '#17a2b8',
  '#2f5fb0',
  '#6f42c1',
  '#e84393',
  '#e67e22',
  '#e4572e',
  '#7cb342',
  '#5d1049',
];

export interface DistrictLine {
  name: string;
  color: string;
  values: number[];
}

export const LOWEST_DISTRICTS: DistrictLine[] = LOWEST_NAMES.map(
  (name, di) => ({
    name,
    color: LOWEST_COLORS[di % LOWEST_COLORS.length],
    values: TREND_DATES.map((_, i) => wobble(83 + di * 0.3, i + di, 1.2)),
  })
);

// ═════════════════════════════════════════════
// Comparison
// ═════════════════════════════════════════════

export interface FullComparisonRow {
  name: string;
  enrolled: number;
  attendance: string;
  compliance: string;
  unmarked: number;
  teacher: string;
  atRisk: string;
  [key: string]: unknown;
}

export const FULL_COMPARISON: FullComparisonRow[] = [
  {
    name: 'Tiruvallur',
    enrolled: 141396,
    attendance: '84.3%',
    compliance: '72.8%',
    unmarked: 4364,
    teacher: '95%',
    atRisk: '7,089 (5.01%)',
  },
  {
    name: 'Chennai',
    enrolled: 239978,
    attendance: '94.2%',
    compliance: '88.2%',
    unmarked: 1418,
    teacher: '93.8%',
    atRisk: '3,302 (1.38%)',
  },
  {
    name: 'Kancheepuram',
    enrolled: 73492,
    attendance: '84.9%',
    compliance: '77.7%',
    unmarked: 1336,
    teacher: '88.1%',
    atRisk: '4,115 (5.6%)',
  },
  {
    name: 'Chengalpattu',
    enrolled: 121448,
    attendance: '91.4%',
    compliance: '92.6%',
    unmarked: 708,
    teacher: '95.5%',
    atRisk: '3,746 (3.08%)',
  },
  {
    name: 'Ranipet',
    enrolled: 87184,
    attendance: '93.9%',
    compliance: '73.6%',
    unmarked: 2539,
    teacher: '90.6%',
    atRisk: '701 (0.8%)',
  },
  {
    name: 'Vellore',
    enrolled: 109672,
    attendance: '81.8%',
    compliance: '90.6%',
    unmarked: 907,
    teacher: '88.1%',
    atRisk: '7,648 (6.97%)',
  },
  {
    name: 'Tirupathur',
    enrolled: 92500,
    attendance: '86.3%',
    compliance: '94.2%',
    unmarked: 415,
    teacher: '95.7%',
    atRisk: '5,485 (5.93%)',
  },
  {
    name: 'Dharmapuri',
    enrolled: 98595,
    attendance: '94.8%',
    compliance: '90.9%',
    unmarked: 1087,
    teacher: '90.6%',
    atRisk: '1,201 (1.22%)',
  },
  {
    name: 'Tiruvannamalai',
    enrolled: 183203,
    attendance: '86.7%',
    compliance: '74.3%',
    unmarked: 5547,
    teacher: '91%',
    atRisk: '7,703 (4.2%)',
  },
  {
    name: 'Villupuram',
    enrolled: 138526,
    attendance: '95.5%',
    compliance: '72.8%',
    unmarked: 4239,
    teacher: '88%',
    atRisk: '651 (0.47%)',
  },
  {
    name: 'Kallakurichi',
    enrolled: 123042,
    attendance: '86.9%',
    compliance: '75.3%',
    unmarked: 2670,
    teacher: '92.2%',
    atRisk: '5,403 (4.39%)',
  },
  {
    name: 'Salem',
    enrolled: 247509,
    attendance: '90.8%',
    compliance: '79.4%',
    unmarked: 5192,
    teacher: '94.5%',
    atRisk: '6,569 (2.65%)',
  },
  {
    name: 'Namakkal',
    enrolled: 102231,
    attendance: '89%',
    compliance: '80.7%',
    unmarked: 3470,
    teacher: '94.4%',
    atRisk: '3,801 (3.72%)',
  },
  {
    name: 'Erode',
    enrolled: 130943,
    attendance: '93.3%',
    compliance: '78%',
    unmarked: 3688,
    teacher: '94.4%',
    atRisk: '1,870 (1.4%)',
  },
  {
    name: 'Tiruppur',
    enrolled: 159136,
    attendance: '87.3%',
    compliance: '84%',
    unmarked: 2689,
    teacher: '88.4%',
    atRisk: '7,882 (4.95%)',
  },
];

// Lowest student-attendance districts for the horizontal bar (ascending value).
export const ATTENDANCE_BARS = [
  { name: 'Theni', value: 85.1 },
  { name: 'Kancheepuram', value: 84.9 },
  { name: 'Thoothukkudi', value: 84.8 },
  { name: 'Tiruvallur', value: 84.3 },
  { name: 'Pudukkottai', value: 83.7 },
  { name: 'Ariyalur', value: 83.1 },
  { name: 'Tirunelveli', value: 82.9 },
  { name: 'Vellore', value: 81.8 },
];

// ═════════════════════════════════════════════
// Alarming
// ═════════════════════════════════════════════

export const ATT_ALARMING_KPIS = {
  highSeverity: 0,
  mediumSeverity: 40,
  totalAlerts: 40,
  districtsFlagged: 26,
};

export const ATT_ALERTS_BY_CATEGORY = {
  categories: [
    'Low Marking Compliance',
    'High Dropout Risk',
    'Low Student Attendance',
    'Low Teacher Attendance',
  ],
  counts: [15, 13, 7, 5],
};

export interface AttRiskPoint {
  name: string;
  compliance: number;
  attendance: number;
  group: 'critical' | 'under-reporting' | 'under-performing' | 'healthy';
}

export const ATT_RISK_POINTS: AttRiskPoint[] = [
  {
    name: 'Villupuram',
    compliance: 72.8,
    attendance: 95.5,
    group: 'under-reporting',
  },
  {
    name: 'Ranipet',
    compliance: 73.6,
    attendance: 93.9,
    group: 'under-reporting',
  },
  {
    name: 'Kancheepuram',
    compliance: 77.7,
    attendance: 84.9,
    group: 'critical',
  },
  {
    name: 'Tiruvannamalai',
    compliance: 74.3,
    attendance: 86.7,
    group: 'critical',
  },
  { name: 'Mayiladuthurai', compliance: 75, attendance: 88, group: 'critical' },
  { name: 'Ariyalur', compliance: 76, attendance: 83.1, group: 'critical' },
  { name: 'Thoothukkudi', compliance: 78, attendance: 84.8, group: 'critical' },
  { name: 'Nagapattinam', compliance: 79, attendance: 85, group: 'critical' },
  {
    name: 'Salem',
    compliance: 79.4,
    attendance: 90.8,
    group: 'under-reporting',
  },
  { name: 'Karur', compliance: 85, attendance: 90, group: 'healthy' },
  { name: 'Erode', compliance: 78, attendance: 93.3, group: 'under-reporting' },
  {
    name: 'Chengalpattu',
    compliance: 92.6,
    attendance: 91.4,
    group: 'healthy',
  },
  { name: 'Dharmapuri', compliance: 90.9, attendance: 94.8, group: 'healthy' },
  { name: 'The Nilgiris', compliance: 88, attendance: 92, group: 'healthy' },
  {
    name: 'Pudukkottai',
    compliance: 88,
    attendance: 83.7,
    group: 'under-performing',
  },
  {
    name: 'Vellore',
    compliance: 90.6,
    attendance: 81.8,
    group: 'under-performing',
  },
  {
    name: 'Tirupathur',
    compliance: 94.2,
    attendance: 86.3,
    group: 'under-performing',
  },
  {
    name: 'Kallakurichi',
    compliance: 75.3,
    attendance: 86.9,
    group: 'critical',
  },
];

export interface DropoutBar {
  name: string;
  pct: number;
  high: boolean;
}

export const POTENTIAL_DROPOUT_BARS: DropoutBar[] = [
  { name: 'Tirunelveli', pct: 7.27, high: true },
  { name: 'Vellore', pct: 6.97, high: true },
  { name: 'Ariyalur', pct: 6.54, high: true },
  { name: 'Pudukkottai', pct: 6.46, high: true },
  { name: 'Theni', pct: 6.07, high: true },
  { name: 'Tirupathur', pct: 5.93, high: true },
  { name: 'Kancheepuram', pct: 5.6, high: true },
  { name: 'Tiruchirappalli', pct: 5.57, high: true },
  { name: 'Virudhunagar', pct: 5.46, high: true },
  { name: 'Cuddalore', pct: 5.4, high: true },
  { name: 'Nagapattinam', pct: 5.09, high: true },
  { name: 'Thoothukkudi', pct: 5.03, high: true },
  { name: 'Tiruvallur', pct: 5.01, high: true },
  { name: 'Tiruppur', pct: 4.95, high: false },
  { name: 'Dindigul', pct: 4.92, high: false },
  { name: 'Sivagangai', pct: 4.85, high: false },
  { name: 'Ramanathapuram', pct: 4.74, high: false },
  { name: 'Kallakurichi', pct: 4.39, high: false },
  { name: 'Coimbatore', pct: 4.35, high: false },
  { name: 'Perambalur', pct: 4.35, high: false },
  { name: 'Tiruvannamalai', pct: 4.2, high: false },
];

export interface AttAlertRow {
  severity: string;
  district: string;
  alert: string;
  measured: string;
  [key: string]: unknown;
}

export const ATT_ALERT_DETAILS: AttAlertRow[] = [
  {
    severity: 'Medium',
    district: 'Tiruvallur',
    alert: 'High Dropout Risk',
    measured: '5.01% absent 15+ days (7,089 students)',
  },
  {
    severity: 'Medium',
    district: 'Thoothukkudi',
    alert: 'High Dropout Risk',
    measured: '5.03% absent 15+ days (6,974 students)',
  },
  {
    severity: 'Medium',
    district: 'Nagapattinam',
    alert: 'High Dropout Risk',
    measured: '5.09% absent 15+ days (2,701 students)',
  },
  {
    severity: 'Medium',
    district: 'Cuddalore',
    alert: 'High Dropout Risk',
    measured: '5.4% absent 15+ days (8,743 students)',
  },
  {
    severity: 'Medium',
    district: 'Virudhunagar',
    alert: 'High Dropout Risk',
    measured: '5.46% absent 15+ days (9,075 students)',
  },
  {
    severity: 'Medium',
    district: 'Tiruchirappalli',
    alert: 'High Dropout Risk',
    measured: '5.57% absent 15+ days (11,997 students)',
  },
  {
    severity: 'Medium',
    district: 'Kancheepuram',
    alert: 'High Dropout Risk',
    measured: '5.6% absent 15+ days (4,115 students)',
  },
  {
    severity: 'Medium',
    district: 'Tirupathur',
    alert: 'High Dropout Risk',
    measured: '5.93% absent 15+ days (5,485 students)',
  },
  {
    severity: 'Medium',
    district: 'Theni',
    alert: 'High Dropout Risk',
    measured: '6.07% absent 15+ days (5,967 students)',
  },
  {
    severity: 'Medium',
    district: 'Pudukkottai',
    alert: 'High Dropout Risk',
    measured: '6.46% absent 15+ days (9,100 students)',
  },
  {
    severity: 'Medium',
    district: 'Ariyalur',
    alert: 'High Dropout Risk',
    measured: '6.54% absent 15+ days (4,009 students)',
  },
  {
    severity: 'Medium',
    district: 'Vellore',
    alert: 'High Dropout Risk',
    measured: '6.9% absent 15+ days (7,648 students)',
  },
  {
    severity: 'Medium',
    district: 'Tirunelveli',
    alert: 'High Dropout Risk',
    measured: '7.27% absent 15+ days (9,527 students)',
  },
  {
    severity: 'Medium',
    district: 'Tiruvallur',
    alert: 'Low Marking Compliance',
    measured: '72.8% marked over last 30 days (state 82.5%)',
  },
  {
    severity: 'Medium',
    district: 'Villupuram',
    alert: 'Low Marking Compliance',
    measured: '72.8% marked over last 30 days (state 82.5%)',
  },
];

export const ATT_ALERT_CATEGORIES = [
  { key: 'all', label: 'All', count: 40 },
  { key: 'compliance', label: 'Low Marking Compliance', count: 15 },
  { key: 'dropout', label: 'High Dropout Risk', count: 13 },
  { key: 'student', label: 'Low Student Attendance', count: 7 },
  { key: 'teacher', label: 'Low Teacher Attendance', count: 5 },
];

// ═════════════════════════════════════════════
// Caution
// ═════════════════════════════════════════════

export interface AttCautionCheck {
  key: string;
  /**
   * The API's numeric `alert_type` for this check, from
   * attendancecautionsummary's `tiles[].type`.
   *
   * This is what ties an entry here to a live tile. The API's own `key`
   * strings do NOT match the `key` field below, so matching on key alone
   * silently failed and the tiles fell back to generic PrimeIcons — the icon
   * and accent chosen here never reached the screen.
   */
  type: number;
  label: string;
  count: number;
  severity: 'High' | 'Medium';
  meaning: string;
  icon: string;
  accent: string;
  [key: string]: unknown;
}

export const ATT_CAUTION_CHECKS: AttCautionCheck[] = [
  {
    key: 'zeroCompliance',
    type: 1,
    label: 'Zero Marking Compliance',
    count: 110,
    severity: 'High',
    meaning: 'Attendance never marked on any of the last 30 working days',
    icon: 'fa-calendar-xmark fa-solid',
    accent: '#c0392b',
  },
  {
    key: 'markUnder25',
    type: 2,
    label: 'Marking Under 25%',
    count: 134,
    severity: 'High',
    meaning: 'Attendance marked on barely any working day',
    icon: 'fa-calendar-day fa-solid',
    accent: '#e4572e',
  },
  {
    key: 'zeroAttendance',
    type: 3,
    label: 'Zero Attendance Recorded',
    count: 110,
    severity: 'High',
    meaning: 'No student ever recorded present',
    icon: 'fa-solid fa-user-slash',
    accent: '#c0392b',
  },
  // {
  //   key: 'zeroTeacher',
  //   label: 'Zero Teacher Attendance',
  //   count: 110,
  //   severity: 'High',
  //   meaning: 'No teacher ever recorded present',
  //   icon: 'pi pi-user-minus',
  //   accent: '#e67e22',
  // },
  // {
  //   key: 'markUnder50',
  //   label: 'Marking Under 50%',
  //   count: 276,
  //   severity: 'Medium',
  //   meaning: 'Attendance marked on less than half the working days',
  //   icon: 'pi pi-exclamation-triangle',
  //   accent: '#b8860b',
  // },
  {
    key: 'dropoutOver20',
    type: 4,
    label: 'Dropout Risk Over 20%',
    count: 2,
    severity: 'Medium',
    meaning: 'More than a fifth of students absent 15+ days',
    icon: 'fa-person-walking-arrow-right fa-solid',
    accent: '#b8860b',
  },
];

export const ATT_CAUTION_TOTAL = 388;

export interface AttCautionSchool {
  district: string;
  block: string;
  school: string;
  udise: string;
  management: string;
  students: number;
  finding: string;
  [key: string]: unknown;
}

export const ATT_CAUTION_SCHOOLS: AttCautionSchool[] = [
  {
    district: 'Villupuram',
    block: 'Koliyanur',
    school:
      'SACRED HEART CONVENT ANGLO INDIAN HIGHER SECONDARY SCHOOL VILLUPURAM',
    udise: '33872400903',
    management: 'Anglo Indian (Partly Aided) School',
    students: 3437,
    finding: '10% marked, 27 days missed',
  },
  {
    district: 'Theni',
    block: 'Uthamapalayam',
    school:
      'SAVARIYAPPA UDAYAR MEMORIAL HIGHER SECONDARY SCHOOL - ROYAPPANPATTY',
    udise: '33250800109',
    management: 'Fully Aided School',
    students: 3222,
    finding: '10% marked, 27 days missed',
  },
  {
    district: 'Tiruvannamalai',
    block: 'Tiruvannamalai',
    school: 'SRI VDS JAIN HSS TIRUVANNAMALAI',
    udise: '33061411341',
    management: 'Fully Aided School',
    students: 1886,
    finding: '16.7% marked, 25 days missed',
  },
  {
    district: 'Chennai',
    block: 'Thiru Vi Ka Nagar',
    school: 'DON BOSCO HR.SEC.SCHOOL',
    udise: '33020400512',
    management: 'Fully Aided School',
    students: 1842,
    finding: '3.3% marked, 29 days missed',
  },
  {
    district: 'Tiruvannamalai',
    block: 'Polur',
    school: 'GHSS (GIRLS) POLUR',
    udise: '33060906204',
    management: 'School Education Department School',
    students: 1623,
    finding: '10% marked, 27 days missed',
  },
  {
    district: 'Vellore',
    block: 'Gudiyatham',
    school: 'GGHSS NADUPET, GUDIYATHAM',
    udise: '33041505111',
    management: 'School Education Department School',
    students: 1612,
    finding: '6.7% marked, 28 days missed',
  },
  {
    district: 'Coimbatore',
    block: 'Periyanaickan Palayam',
    school: 'GHSS ASOKAPURAM -MODEL',
    udise: '33120300709',
    management: 'School Education Department School',
    students: 1358,
    finding: '23.3% marked, 23 days missed',
  },
  {
    district: 'Tenkasi',
    block: 'Keelapavoor',
    school: 'GHSS POOLANGULAM',
    udise: '33290703305',
    management: 'School Education Department School',
    students: 1007,
    finding: '13.3% marked, 26 days missed',
  },
  {
    district: 'Madurai',
    block: 'Melur',
    school: 'TAMILARASI MIDDLE SCHOOL, MELUR',
    udise: '33240800801',
    management: 'Fully Aided School',
    students: 953,
    finding: '6.7% marked, 27 days missed',
  },
  {
    district: 'Mayiladuthurai',
    block: 'Kuthalam',
    school: 'GHSS KOMAL',
    udise: '33190803704',
    management: 'School Education Department School',
    students: 935,
    finding: '10% marked, 27 days missed',
  },
  {
    district: 'Ramanathapuram',
    block: 'Paramakudi',
    school: 'S.N.V. GOVERNMENT MODEL HIGHER SECONDARY SCHOOL, EMANESWARAM',
    udise: '33270309204',
    management: 'School Education Department School',
    students: 871,
    finding: '10% marked, 27 days missed',
  },
  {
    district: 'Salem',
    block: 'Panamarathupatti',
    school: 'GOVT HR SEC SCHOOL, NEIKARAPATTI, SALEM - 636010',
    udise: '33080608205',
    management: 'School Education Department School',
    students: 747,
    finding: '6.7% marked, 27 days missed',
  },
];
